import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    ArrowUpRight,
    Star,
    BadgeCheck,
    ShieldCheck,
    HardHat,
    CalendarCheck,
    FileCheck2,
    MapPin,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import LogoFull from "@/components/ui/LogoFull";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { TRACK_LIST, TRACKS, getPreferredTrack, setPreferredTrack } from "@/lib/tracks";
import { PAGE_META, AGGREGATE_RATING, EXTERNAL_PROFILES } from "@/lib/seo-constants";
import { localBusinessSchema, websiteSchema, faqPageSchema } from "@/lib/jsonld";
import { Section } from "@/components/ui/section";
import FAQAccordion from "@/components/FAQAccordion";
import { LOCATIONS } from "@/data/locations";
import { usePageContentOverrideMap } from "@/hooks/usePageContentOverrides";
import ResponsiveImg from "@/components/ResponsiveImg";

const TrackCard = ({ track, override }) => (
    <Link
        to={track.home}
        onClick={() => setPreferredTrack(track.key)}
        className="group relative flex flex-col justify-end overflow-hidden border border-white/10 bg-brand-dark min-h-[320px] md:min-h-[420px] transition-all duration-200 hover:border-brand-cyan hover:-translate-y-1"
        data-testid={`splash-card-${track.key}`}
    >
        <ResponsiveImg
            src={override?.hero_image || track.image}
            alt={
                override?.hero_image_alt ||
                `${track.label} contracting — ${track.cardTitle}`
            }
            className="absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-500 group-hover:scale-105"
            sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
            loading="eager"
        />
        <div
            className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/55 to-brand-dark/10"
            aria-hidden="true"
        />
        <div className="relative z-10 p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-2">
                {track.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white leading-none">
                {track.cardTitle}
            </h2>
            <p className="mt-3 text-sm text-white/75 leading-relaxed max-w-sm">
                {track.cardBlurb}
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white group-hover:text-brand-cyan transition-colors">
                Enter {track.label}
                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </span>
        </div>
    </Link>
);

// ---------------------------------------------------------------------------
// Below-the-fold content for "/" — the site's highest-authority URL. Every
// trust claim here is already stated elsewhere on the site (reviews data,
// BBB seal, About page); don't add claims that can't be backed up.
// ---------------------------------------------------------------------------
const TRUST = [
    {
        icon: Star,
        title: `${AGGREGATE_RATING.average.toFixed(1)}★ across ${AGGREGATE_RATING.total} reviews`,
        body: "Verified on Google, HomeStars, Bark, Houzz and BBB.",
        to: "/reviews",
        cta: "Read reviews",
    },
    {
        icon: BadgeCheck,
        title: "BBB Accredited · A+",
        body: "Accredited business with an A+ rating from the Better Business Bureau.",
        href: EXTERNAL_PROFILES.bbb,
        cta: "BBB profile",
    },
    {
        icon: ShieldCheck,
        title: "Licensed & insured",
        body: "$5M liability insurance and WSIB coverage on every crew. COIs on request for commercial work.",
    },
    {
        icon: HardHat,
        title: "Building since 2018",
        body: "Founded in North York by Shaheen Rashidi — one accountable team from quote to sign-off.",
        to: "/about",
        cta: "About us",
    },
    {
        icon: CalendarCheck,
        title: "Free on-site quotes",
        body: "We reply within one business day and give you a fixed written price — no surprise extras.",
        to: "/contact",
        cta: "Get a quote",
    },
    {
        icon: FileCheck2,
        title: "Permits handled",
        body: "We check bylaws for your address and pull the permits for decks, pool enclosures and driveway widening.",
        to: "/service-area",
        cta: "Local bylaws",
    },
];

const TrustSignals = () => (
    <Section bg="white" data-testid="splash-trust">
        <Container>
            <div className="max-w-2xl mb-10 md:mb-12">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    Why Reformity
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    GTA contractors you can check up on.
                </h2>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {TRUST.map(({ icon: Icon, title, body, to, href, cta }) => (
                    <li key={title} className="border border-brand-dark/10 bg-brand-offwhite p-6">
                        <Icon className="h-7 w-7 text-brand-cyan" strokeWidth={2} />
                        <h3 className="mt-4 font-sans font-semibold text-lg text-brand-dark">{title}</h3>
                        <p className="mt-2 text-sm text-brand-dark/70 leading-relaxed">{body}</p>
                        {to && (
                            <Link to={to} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-cyan hover:text-brand-coral">
                                {cta} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
                            </Link>
                        )}
                        {href && (
                            <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-cyan hover:text-brand-coral">
                                {cta} <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
                            </a>
                        )}
                    </li>
                ))}
            </ul>
        </Container>
    </Section>
);

const ServicesAndAreas = () => (
    <Section data-testid="splash-services-areas">
        <Container>
            <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    Services &amp; Areas
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    Everything we build, everywhere we work.
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {TRACK_LIST.map((t) => (
                    <div key={t.key}>
                        <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-3">
                            <Link to={t.home} className="hover:text-brand-cyan">{t.label}</Link>
                        </h3>
                        <ul className="space-y-2">
                            {t.services.map((sv) => (
                                <li key={sv.path}>
                                    <Link to={sv.path} className="text-sm text-brand-dark/75 hover:text-brand-cyan">
                                        {sv.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                <div>
                    <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-3">
                        <Link to="/service-area" className="hover:text-brand-cyan">Areas</Link>
                    </h3>
                    <ul className="space-y-2">
                        {LOCATIONS.map((l) => (
                            <li key={l.slug}>
                                <Link to={`/locations/${l.slug}`} className="inline-flex items-center gap-1.5 text-sm text-brand-dark/75 hover:text-brand-cyan">
                                    <MapPin className="h-3.5 w-3.5 text-brand-cyan" strokeWidth={2.5} />
                                    {l.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </Container>
    </Section>
);

const HOME_FAQS = [
    {
        q: "What does Reformity Inc. do?",
        a: "We're a North York-based exterior and general contractor. For homeowners we build fences, decks, interlocking, concrete and asphalt driveways, landscaping, masonry and interior renovations. For commercial and industrial properties we handle fencing and gates, parking lots and paving, concrete, masonry restoration, grounds and snow and ice management.",
    },
    {
        q: "Which areas do you serve?",
        a: "The Greater Toronto Area and Golden Horseshoe — including Toronto, North York, Etobicoke, Brampton, Mississauga, Vaughan, Markham, Richmond Hill, Oakville, Burlington and Hamilton. Our head office is at 5700 Yonge St in North York.",
    },
    {
        q: "Are you licensed and insured?",
        a: "Yes. We carry $5M liability insurance and WSIB coverage, and we provide certificates of insurance and WSIB clearance for commercial and industrial clients on request.",
    },
    {
        q: "How do quotes work, and are they free?",
        a: "Quotes are free. Send the form or call 437-241-6287 and we'll reply within one business day, visit the property to measure and check the bylaws for your address, and give you a fixed written price.",
    },
    {
        q: "Do you handle permits?",
        a: "Yes. We check fence, pool, tree and driveway rules for your municipality and apply for the permits a project needs — decks, pool enclosures, curb cuts and driveway widening are the common ones.",
    },
    {
        q: "Do you work for property managers and industrial facilities?",
        a: "Yes — single sites or whole portfolios. We schedule around tenants and operations, provide COIs and WSIB clearance, and give you one point of contact across trades.",
    },
    {
        q: "What are your hours?",
        a: "Monday to Saturday, 8:00 AM to 6:00 PM. You can send a quote request any time and we'll reply within one business day.",
    },
];

const HomeFaq = () => (
    <Section bg="white" data-testid="splash-faq">
        <Container size="narrow">
            <div className="mb-8 md:mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    FAQ
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    Questions we hear most.
                </h2>
            </div>
            <FAQAccordion items={HOME_FAQS} testidPrefix="splash-faq" />
        </Container>
    </Section>
);

export default function Splash() {
    const preferred = getPreferredTrack();
    const preferredTrack = preferred ? TRACKS[preferred] : null;
    const overrideMap = usePageContentOverrideMap();

    return (
        <>
            <Seo
                {...PAGE_META.splash}
                path="/"
                jsonLd={[localBusinessSchema(), websiteSchema(), faqPageSchema(HOME_FAQS)]}
            />

            <section
                className="relative bg-brand-dark text-white"
                data-testid="splash"
            >
                <Container className="py-14 md:py-20">
                    {/* Brand + headline */}
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="flex justify-center mb-8">
                            <LogoFull theme="dark" className="h-14 md:h-16" />
                        </div>
                        {/* Keyword line inside the <h1> so the site's strongest URL
                            targets "general contractor GTA"; the display line stays
                            the brand tagline. */}
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-white">
                            <span className="block font-sans text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-cyan font-semibold leading-normal mb-5">
                                GTA Exterior &amp; General Contractors
                            </span>
                            Built for every property.
                        </h1>
                        <p className="mt-5 text-base md:text-lg text-white/75 font-light italic">
                            Residential · Commercial · Industrial — exterior &amp; general
                            contracting across the GTA.
                        </p>

                        {/* Return-visit soft suggestion (never traps — all three still shown below) */}
                        {preferredTrack && (
                            <div className="mt-6">
                                <Link
                                    to={preferredTrack.home}
                                    onClick={() => setPreferredTrack(preferredTrack.key)}
                                    className="inline-flex items-center gap-2 border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-2 text-sm font-semibold text-brand-cyan hover:bg-brand-cyan hover:text-brand-dark transition-colors"
                                    data-testid="splash-continue"
                                >
                                    Continue to {preferredTrack.label}
                                    <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Three track cards */}
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                        {TRACK_LIST.map((t) => (
                            <TrackCard
                                key={t.key}
                                track={t}
                                override={overrideMap[`splash-card:${t.key}`]}
                            />
                        ))}
                    </div>

                    {/* Persistent CTAs */}
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <BrandButton asChild variant="primary" size="lg">
                            <Link to="/contact" data-testid="splash-cta-quote">
                                Get a Free Quote
                                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                            </Link>
                        </BrandButton>
                        <a
                            href={COMPANY.phoneHref}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-brand-cyan transition-colors"
                            data-testid="splash-phone"
                        >
                            <Phone className="h-4 w-4 text-brand-coral" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </div>

                    {/* Crawlable intro — link equity to all three tracks (not a JS-only dead-end) */}
                    <div className="mt-14 pt-10 border-t border-white/10 max-w-3xl mx-auto text-center">
                        <p className="text-sm text-white/60 leading-relaxed">
                            <strong className="text-white/80">Reformity Inc.</strong> is a
                            North York-based exterior and general contracting company serving
                            the Greater Toronto Area. We build and maintain fences, decks,
                            interlocking, concrete, asphalt, landscaping, masonry and
                            interior renovations for{" "}
                            <Link to="/residential" className="text-brand-cyan hover:underline">
                                residential homeowners
                            </Link>
                            ,{" "}
                            <Link to="/commercial" className="text-brand-cyan hover:underline">
                                commercial property owners and managers
                            </Link>
                            , and{" "}
                            <Link to="/industrial" className="text-brand-cyan hover:underline">
                                industrial facilities
                            </Link>{" "}
                            across the GTA. Licensed, insured, and WSIB-compliant.
                        </p>
                    </div>
                </Container>
            </section>

            <TrustSignals />
            <ServicesAndAreas />
            <HomeFaq />
        </>
    );
}
